import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { fetchProducts, formatMinor } from '@/api/commercial';
import type { ProductData, ProductKind } from '@/types/generated';

const KINDS: { value: ProductKind | ''; label: string }[] = [
  { value: '', label: 'All kinds' },
  { value: 'freight', label: 'Freight' },
  { value: 'accessorial', label: 'Accessorial' },
  { value: 'surcharge', label: 'Surcharge' },
  { value: 'disbursement', label: 'Disbursement' },
];

const KIND_COLOUR: Record<ProductKind, 'success' | 'info' | 'warning' | 'default'> = {
  freight: 'success',
  accessorial: 'info',
  surcharge: 'warning',
  disbursement: 'default',
};

/**
 * The service catalogue.
 *
 * The rate column is the one thing here that must not be got wrong: the same
 * field is minor currency units for most entries and BASIS POINTS for a
 * percentage one. The API sends `is_proportional` and a `formatted_rate` computed
 * server-side precisely so a client never has to infer that — rendering 1250
 * basis points as "KES 12.50" is a customer-facing error, and both readings look
 * plausible.
 */
export function CatalogPage() {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<ProductKind | ''>('');
  const [activeOnly, setActiveOnly] = useState(false);

  const debouncedSearch = useDebounced(search, 300);

  const query = useInfiniteQuery({
    queryKey: ['products', { search: debouncedSearch, kind, activeOnly }],
    queryFn: ({ pageParam }) =>
      fetchProducts({
        search: debouncedSearch,
        kind: kind || undefined,
        activeOnly,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });

  const products = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="h5">Catalogue</Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Code or name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 240 }}
        />

        <TextField
          size="small"
          select
          label="Kind"
          value={kind}
          onChange={(event) => setKind(event.target.value as ProductKind | '')}
          sx={{ minWidth: 170 }}
        >
          {KINDS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Off by default: this screen is where someone goes to find a withdrawn
            service, so hiding them by default hides the reason for the visit. */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.target.checked)}
            />
          }
          label="Quotable only"
        />
      </Stack>

      {query.isError && <Alert severity="error">Could not load the catalogue.</Alert>}

      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell>Minimum</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isPending && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}

            {!query.isPending && products.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nothing matches those filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {products.map((product) => (
              <TableRow key={product.id} hover sx={{ opacity: product.is_active ? 1 : 0.55 }}>
                <TableCell>{product.code}</TableCell>
                <TableCell>
                  {product.name}
                  {!product.is_active && (
                    <Chip size="small" label="withdrawn" sx={{ ml: 1 }} variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={product.kind} color={KIND_COLOUR[product.kind]} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{product.category.replace(/_/g, ' ')}</Typography>
                </TableCell>
                <TableCell align="right">
                  <RateCell product={product} />
                </TableCell>
                <TableCell>
                  {product.minimum_charge_minor === null
                    ? '—'
                    : formatMinor(product.minimum_charge_minor, product.currency ?? '')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {query.hasNextPage && (
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

/**
 * The rate, rendered from the server's own formatting.
 *
 * Deliberately NOT recomputed here. `default_rate_minor` means two different
 * things depending on the unit, and one correct implementation on the side that
 * owns the rule beats a second plausible one in the client.
 */
function RateCell({ product }: { product: ProductData }) {
  return (
    <Tooltip
      title={
        product.is_proportional
          ? 'A proportion of a subtotal, stored as basis points'
          : `Charged per ${product.unit_of_measure.replace(/_/g, ' ')}`
      }
    >
      <span>{product.formatted_rate}</span>
    </Tooltip>
  );
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);

    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}
